import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text, Flex } from 'components/ui';
import StarRating from 'components/StarRating/StarRating';
import ReviewCount from 'components/Product/ReviewCount/ReviewCount';
import CompareZoneBase from 'components/Catalog/ProductGrid/CompareModal/Zones/CompareZoneBase';
import { EmptyState } from 'components/Catalog/ProductGrid/hooks/useComparisonSkus';
import { useComparisonSkus } from 'components/Catalog/ProductGrid/hooks/useComparisonSkus';
import { SENTIMENT_TYPE } from 'components/Catalog/ProductGrid/CompareModal/constants';
import { space, lineHeights } from 'style/config';

const RatingsAndReviewsZone = ({ comparisonData = {}, getText = () => {} }) => {
    const skus = useComparisonSkus(comparisonData);

    const renderContent = (sku, _index) => {
        const parentProduct = sku?.parentProducts?.[0];

        if (!parentProduct) {
            return <EmptyState />;
        }

        const { averageRating, totalReviews } = parentProduct?.ratings || {};
        const sentiments = parentProduct?.sentiments || [];

        // Filter pros and cons
        const pros = sentiments.filter(s => s.type === SENTIMENT_TYPE.PROS);
        const cons = sentiments.filter(s => s.type === SENTIMENT_TYPE.CONS);

        // Check if we have any data to show
        const hasRating = averageRating && averageRating > 0;

        if (!hasRating) {
            return <EmptyState />;
        }

        return (
            <Flex
                flexDirection='column'
                gap={4}
            >
                {hasRating && (
                    <Flex
                        alignItems='center'
                        gap={2}
                        lineHeight='none'
                    >
                        <Text
                            fontSize='base'
                            fontWeight='bold'
                            is='p'
                        >
                            {averageRating.toFixed(1)}
                        </Text>
                        <StarRating
                            size={12}
                            rating={averageRating}
                        />
                        {totalReviews > 0 && <ReviewCount productReviewCount={totalReviews} />}
                    </Flex>
                )}

                {pros.length > 0 && (
                    <Flex flexDirection='column'>
                        <Text
                            fontSize='sm'
                            paddingBottom={2}
                            lineHeight='none'
                            color='gray'
                            is='p'
                        >
                            {getText('prosMentioned')}
                        </Text>
                        <Box
                            is='ul'
                            css={styles.sentimentList}
                        >
                            {pros.map((pro, idx) => (
                                <Text
                                    key={`pro-${idx}`}
                                    is='li'
                                    fontSize='base'
                                >
                                    {pro.sentiment} ({pro.count})
                                </Text>
                            ))}
                        </Box>
                    </Flex>
                )}

                {cons.length > 0 && (
                    <Flex flexDirection='column'>
                        <Text
                            fontSize='sm'
                            color='gray'
                            paddingBottom={2}
                            lineHeight='none'
                            is='p'
                        >
                            {getText('consMentioned')}
                        </Text>
                        <Box
                            is='ul'
                            css={styles.sentimentList}
                        >
                            {cons.map((con, idx) => (
                                <Text
                                    key={`con-${idx}`}
                                    is='li'
                                    fontSize='base'
                                >
                                    {con.sentiment} ({con.count})
                                </Text>
                            ))}
                        </Box>
                    </Flex>
                )}
            </Flex>
        );
    };

    return (
        <CompareZoneBase
            selectedProducts={skus}
            getText={getText}
            titleKey='ratingsAndReviews'
            renderContent={renderContent}
        />
    );
};

const styles = {
    sentimentList: {
        listStyleType: 'disc',
        paddingLeft: `${space[5]}px`,
        lineHeight: lineHeights.tight
    }
};

RatingsAndReviewsZone.propTypes = {
    comparisonData: PropTypes.object,
    getText: PropTypes.func
};

export default wrapFunctionalComponent(RatingsAndReviewsZone, 'RatingsAndReviewsZone');
